import {
    ChangingBudgetImportEntryDTO,
    ChangingEmployeeGroupImportEntryDTO,
    ChangingOfficeImportEntryDTO,
    ChangingRegisteredEmployeeGroupImportEntryDTO,
    ChangingTimeInLieuImportEntryDTO,
    CustomerReportingDataImportEntryDTO,
    LeavingEmployeesImportEntryDTO,
    NewEmployeesImportEntryDTO,
    SmartAssistantImportColumnKey,
    SmartAssistantImportEntryStatus,
    SmartAssistantImportValidationError,
    SmartAssistantType,
    SolutionPartnerIdentificationNumberImportEntryDTO,
    UrbanSportsClubMembershipStatusImportEntryDTO
} from '@iota/inh-core-v1-management-client';
import {
    SmartTableCell,
    SmartTableHeader,
    SmartTableHeaderAndData,
    SmartTableRow
} from 'src/app/shared/components/smart-table/smart-table.component';
import {
    SMART_ASSISTANT_COLUMN_KEY_LABELS,
    SmartAssistantValidationResult,
    isCustomVariableColumn,
    sortColumnKeysByGlobalOrder,
    toColumnLabel
} from 'src/app/shared/constants-and-enums/smart-assistant-types';
import { SMART_ASSISTANT_VALIDATION_ERROR_LABELS } from 'src/app/shared/constants-and-enums/smart-assistant-validation-errors';
import { formatDate, getPropertyAsString } from 'src/app/shared/utils';

type CellFormat = 'money' | 'date' | 'number' | 'text';

interface ValidationColumn<TEntry> {
    field: keyof TEntry & string;
    label: string;
    format: CellFormat;
    replacedBy?: keyof TEntry & string;
}

interface ValidationEntry {
    status: SmartAssistantImportEntryStatus;
    validationError?: SmartAssistantImportValidationError | null;
    earliestChangeDate?: string;
}

const EMPTY_TABLE: SmartTableHeaderAndData = { headers: [], rows: [] };

export function toSmartAssistantValidationTable(
    importType: SmartAssistantType | null,
    result: SmartAssistantValidationResult | null,
    columns: SmartAssistantImportColumnKey[],
    columnLabels: { [key: string]: string },
    showUnchanged: boolean
): SmartTableHeaderAndData {
    if (!importType || !result) {
        return EMPTY_TABLE;
    }
    const allEntries: ValidationEntry[] = result.entries ?? [];
    const entries = allEntries.filter((entry) => showUnchanged || entry.status !== SmartAssistantImportEntryStatus.UNCHANGED);

    switch (importType) {
        case SmartAssistantType.EMPLOYEE_SOLUTION_PARTNER_IDENTIFICATION_NUMBER_IMPORT:
            return toIdentificationNumberTable(entries as SolutionPartnerIdentificationNumberImportEntryDTO[]);
        case SmartAssistantType.URBAN_SPORTS_CLUB_MEMBERSHIP_STATUS_IMPORT:
            return toUrbanSportsClubMembershipTable(entries as UrbanSportsClubMembershipStatusImportEntryDTO[]);
        case SmartAssistantType.CHANGING_BUDGET:
            return toCustomerTable<ChangingBudgetImportEntryDTO>(entries as ChangingBudgetImportEntryDTO[], [
                ...employeeColumns(),
                { field: 'oldYearlyBudget', label: $localize`Previous budget`, format: 'money', replacedBy: 'newYearlyBudget' },
                { field: 'newYearlyBudget', label: $localize`New budget`, format: 'money' }
            ]);
        case SmartAssistantType.CHANGING_EMPLOYEE_GROUP:
            return toCustomerTable<ChangingEmployeeGroupImportEntryDTO>(entries as ChangingEmployeeGroupImportEntryDTO[], [
                ...employeeColumns(),
                ...employeeGroupColumns()
            ]);
        case SmartAssistantType.CHANGING_REGISTERED_EMPLOYEE_GROUP:
            return toCustomerTable<ChangingRegisteredEmployeeGroupImportEntryDTO>(
                entries as ChangingRegisteredEmployeeGroupImportEntryDTO[],
                [
                    ...employeeColumns(),
                    ...employeeGroupColumns(),
                    { field: 'oldChangeDate', label: $localize`Previous group change date`, format: 'date', replacedBy: 'newChangeDate' },
                    { field: 'newChangeDate', label: $localize`New group change date`, format: 'date' }
                ]
            );
        case SmartAssistantType.CHANGING_OFFICE:
            return toCustomerTable<ChangingOfficeImportEntryDTO>(entries as ChangingOfficeImportEntryDTO[], [
                ...employeeColumns(),
                { field: 'oldOfficeName', label: $localize`Previous office`, format: 'text', replacedBy: 'newOfficeName' },
                { field: 'newOfficeName', label: $localize`New office`, format: 'text' }
            ]);
        case SmartAssistantType.LEAVING_EMPLOYEES:
            return toCustomerTable<LeavingEmployeesImportEntryDTO>(entries as LeavingEmployeesImportEntryDTO[], [
                ...employeeColumns(),
                { field: 'oldLeavingDate', label: $localize`Previous leaving date`, format: 'date', replacedBy: 'newLeavingDate' },
                { field: 'newLeavingDate', label: $localize`New leaving date`, format: 'date' },
                { field: 'licenseEndDate', label: $localize`License end`, format: 'date' }
            ]);
        case SmartAssistantType.NEW_EMPLOYEES:
            return toCustomerTable<NewEmployeesImportEntryDTO>(entries as NewEmployeesImportEntryDTO[], [
                { field: 'employeeNumber', label: SMART_ASSISTANT_COLUMN_KEY_LABELS.EMPLOYEE_NUMBER, format: 'text' },
                { field: 'dateOfBirth', label: SMART_ASSISTANT_COLUMN_KEY_LABELS.DATE_OF_BIRTH, format: 'date' },
                { field: 'officeName', label: SMART_ASSISTANT_COLUMN_KEY_LABELS.OFFICE_NAME, format: 'text' },
                { field: 'employeeGroup', label: SMART_ASSISTANT_COLUMN_KEY_LABELS.EMPLOYEE_GROUP, format: 'text' },
                { field: 'yearlyBudget', label: SMART_ASSISTANT_COLUMN_KEY_LABELS.YEARLY_BUDGET, format: 'money' },
                {
                    field: 'registrationPossibleFrom',
                    label: SMART_ASSISTANT_COLUMN_KEY_LABELS.REGISTRATION_POSSIBLE_FROM,
                    format: 'date'
                },
                {
                    field: 'alternativeEmployeeNumber',
                    label: SMART_ASSISTANT_COLUMN_KEY_LABELS.ALTERNATIVE_EMPLOYEE_NUMBER,
                    format: 'text'
                }
            ]);
        case SmartAssistantType.CHANGING_TIME_IN_LIEU:
            return toCustomerTable<ChangingTimeInLieuImportEntryDTO>(entries as ChangingTimeInLieuImportEntryDTO[], [
                ...employeeColumns(),
                {
                    field: 'oldWorkingDaysWeekly',
                    label: $localize`Previous working days per week`,
                    format: 'number',
                    replacedBy: 'newWorkingDaysWeekly'
                },
                { field: 'newWorkingDaysWeekly', label: $localize`New working days per week`, format: 'number' },
                {
                    field: 'oldWorkingHoursWeekly',
                    label: $localize`Previous weekly working hours`,
                    format: 'number',
                    replacedBy: 'newWorkingHoursWeekly'
                },
                { field: 'newWorkingHoursWeekly', label: $localize`New weekly working hours`, format: 'number' },
                {
                    field: 'oldFullTimeEquivalent',
                    label: $localize`Previous FTE`,
                    format: 'number',
                    replacedBy: 'newFullTimeEquivalent'
                },
                { field: 'newFullTimeEquivalent', label: $localize`New FTE`, format: 'number' }
            ]);
        case SmartAssistantType.CUSTOMER_REPORTING_DATA:
            return toCustomerReportingDataTable(entries as CustomerReportingDataImportEntryDTO[], columns, columnLabels);
        default:
            return EMPTY_TABLE;
    }
}

function employeeColumns<TEntry extends { employeeNumber?: string; employeeName?: string }>(): ValidationColumn<TEntry>[] {
    return [
        { field: 'employeeNumber', label: SMART_ASSISTANT_COLUMN_KEY_LABELS.EMPLOYEE_NUMBER, format: 'text' },
        { field: 'employeeName', label: $localize`User Name`, format: 'text' }
    ];
}

function employeeGroupColumns<TEntry extends { oldEmployeeGroup?: string; newEmployeeGroup?: string }>(): ValidationColumn<TEntry>[] {
    return [
        {
            field: 'oldEmployeeGroup',
            label: $localize`Previous employee group`,
            format: 'text',
            replacedBy: 'newEmployeeGroup'
        },
        { field: 'newEmployeeGroup', label: $localize`New employee group`, format: 'text' }
    ];
}

function toCustomerTable<TEntry extends ValidationEntry>(entries: TEntry[], columns: ValidationColumn<TEntry>[]): SmartTableHeaderAndData {
    const headers: SmartTableHeader[] = [...columns.map(({ field, label }) => ({ key: field, label })), remarkHeader()];
    const rows = entries.map((entry): SmartTableRow => {
        const row: SmartTableRow = { remark: toRemarkCell(entry) };
        columns.forEach(({ field, format, replacedBy }) => {
            const crossedOut = !!replacedBy && isReplaced(entry, entry[field], entry[replacedBy]);
            row[field] = { ...toFormattedCell(entry[field], format), ...(crossedOut ? { crossedOut } : {}) };
        });
        return row;
    });
    return { headers, rows };
}

function toCustomerReportingDataTable(
    entries: CustomerReportingDataImportEntryDTO[],
    columns: SmartAssistantImportColumnKey[],
    columnLabels: { [key: string]: string }
): SmartTableHeaderAndData {
    const variableKeys = sortColumnKeysByGlobalOrder(columns.filter(isCustomVariableColumn));
    const variableHeaders = variableKeys.flatMap((key): SmartTableHeader[] => {
        const label = toColumnLabel(key, columnLabels);
        return [
            { key: `old-${key}`, label: $localize`Previous ${label}:label:` },
            { key: `new-${key}`, label: $localize`New ${label}:label:` }
        ];
    });
    const headers: SmartTableHeader[] = [
        { key: 'employeeNumber', label: SMART_ASSISTANT_COLUMN_KEY_LABELS.EMPLOYEE_NUMBER },
        { key: 'employeeName', label: $localize`User Name` },
        ...variableHeaders,
        remarkHeader()
    ];

    const rows = entries.map((entry): SmartTableRow => {
        const row: SmartTableRow = {
            employeeNumber: toFormattedCell(entry.employeeNumber, 'text'),
            employeeName: toFormattedCell(entry.employeeName, 'text'),
            remark: toRemarkCell(entry)
        };
        variableKeys.forEach((key) => {
            const variable = (entry.variables ?? []).find((item) => item.columnKey === key);
            const crossedOut = isReplaced(entry, variable?.oldValue, variable?.newValue);
            row[`old-${key}`] = { data: variable?.oldValue ?? '', ...(crossedOut ? { crossedOut } : {}) };
            row[`new-${key}`] = { data: variable?.newValue ?? '' };
        });
        return row;
    });
    return { headers, rows };
}

function toIdentificationNumberTable(entries: SolutionPartnerIdentificationNumberImportEntryDTO[]): SmartTableHeaderAndData {
    const headers: SmartTableHeader[] = [
        { key: 'chaempId', label: $localize`chæmp-ID` },
        { key: 'oldIdentificationNumber', label: $localize`Previous ID-No.` },
        { key: 'newIdentificationNumber', label: $localize`New ID-No.` },
        { key: 'employeeName', label: $localize`User Name` },
        { key: 'productName', label: $localize`Product` },
        remarkHeader()
    ];

    const rows = entries.map((entry): SmartTableRow => {
        const hasChanged = !!entry.newIdentificationNumber && entry.oldIdentificationNumber !== entry.newIdentificationNumber;
        return {
            chaempId: { data: entry.chaempId ?? '' },
            oldIdentificationNumber: { data: entry.oldIdentificationNumber ?? '', crossedOut: hasChanged },
            newIdentificationNumber: { data: entry.newIdentificationNumber ?? '' },
            employeeName: { data: entry.employeeName ?? '' },
            productName: { data: entry.productName ?? '' },
            remark: toRemarkCell(entry)
        };
    });
    return { headers, rows };
}

function toUrbanSportsClubMembershipTable(entries: UrbanSportsClubMembershipStatusImportEntryDTO[]): SmartTableHeaderAndData {
    const headers: SmartTableHeader[] = [
        { key: 'chaempId', label: $localize`chæmp-ID` },
        { key: 'subscriptionActive', label: $localize`Subscription Active` },
        { key: 'previousPrice', label: $localize`Previous Price` },
        { key: 'newPrice', label: $localize`New Price` },
        { key: 'employeeName', label: $localize`User Name` },
        remarkHeader()
    ];

    const rows = entries.map((entry): SmartTableRow => {
        const hasChanged = entry.newCompanyCostsMonthly != null && entry.previousCompanyCostsMonthly !== entry.newCompanyCostsMonthly;
        return {
            chaempId: { data: entry.chaempId ?? '' },
            subscriptionActive: { data: formatDate(entry.budgetTo) ?? '' },
            previousPrice: {
                data: getPropertyAsString(entry.previousCompanyCostsMonthly, { formatNumberAsCurrency: true }),
                suffix: ' €',
                crossedOut: hasChanged
            },
            newPrice: { data: getPropertyAsString(entry.newCompanyCostsMonthly, { formatNumberAsCurrency: true }), suffix: ' €' },
            employeeName: { data: entry.employeeName ?? '' },
            remark: toRemarkCell(entry)
        };
    });
    return { headers, rows };
}

function remarkHeader(): SmartTableHeader {
    return { key: 'remark', label: $localize`Remark` };
}

function isReplaced(entry: ValidationEntry, oldValue: unknown, newValue: unknown): boolean {
    return entry.status === SmartAssistantImportEntryStatus.CHANGED && oldValue !== newValue;
}

function toFormattedCell(value: unknown, format: CellFormat): SmartTableCell {
    switch (format) {
        case 'money':
            return { data: getPropertyAsString(value, { formatNumberAsCurrency: true }), suffix: ' €' };
        case 'date':
            return { data: formatDate(value as string | undefined) ?? '' };
        case 'number':
            return { data: value == null ? '' : String(value) };
        default:
            return { data: (value as string | undefined) ?? '' };
    }
}

function toRemarkCell(entry: ValidationEntry): SmartTableCell {
    const isError = entry.status === SmartAssistantImportEntryStatus.VALIDATION_ERROR;
    const label = isError ? toRemarkLabel(entry) : '';
    return { data: '', rawData: { isError, label }, sortValue: label };
}

function toRemarkLabel(entry: ValidationEntry): string {
    if (!entry.validationError) {
        return '';
    }
    const label = SMART_ASSISTANT_VALIDATION_ERROR_LABELS[entry.validationError] ?? entry.validationError;
    if (entry.validationError === SmartAssistantImportValidationError.GROUP_CHANGE_DATE_TOO_EARLY && entry.earliestChangeDate) {
        return `${label} (${$localize`earliest possible: ${formatDate(entry.earliestChangeDate)}:date:`})`;
    }
    return label;
}
